import { type ReactNode, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';

import { pxToRem } from '@yoweb/ui/styles/utils';
import { th } from '@yoweb/ui/styles/utils/theme';
import { useClickOutside } from '@yoweb/ui/hooks/useClickOutside';
import { Box } from '@yoweb/ui/components/Box';
import type { BoxProps } from '@yoweb/ui/components/Box';

const numberPattern = /\d+/g;

const getBorderBottomWidth = (element: Element) => {
  const borderBottomWidthPx = getComputedStyle(element, null).getPropertyValue(
    'border-bottom-width',
  );

  const borderBottomWidth = borderBottomWidthPx.match(numberPattern);

  if (borderBottomWidth) {
    return parseInt(borderBottomWidth[0], 10);
  }

  // Default to 1px which is set in css
  return 1;
};

const optionsVariants = {
  initial: { opacity: 0, y: -5 },
  animate: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, type: 'spring' },
  },
  exit: { opacity: 0, y: -5, transition: { duration: 0.2 } },
};

type CustomOptionsProps = {
  onClose(): void;
  maxHeightOptions: number;
  optionIndex: number | null;
  id: string;
  label: string;
  children?: ReactNode;
};

export const CustomOptions = ({
  onClose,
  children,
  maxHeightOptions,
  optionIndex,
  id,
  label,
}: CustomOptionsProps) => {
  const optionsRef = useRef<HTMLDivElement>(null);

  useClickOutside(optionsRef, onClose);

  useEffect(() => {
    const element = optionsRef.current?.children?.[0];

    if (!element || !optionsRef.current || optionIndex === null) return;

    const optionHeight = element.clientHeight + getBorderBottomWidth(element); // option height + border-bottom-width
    const optionsHeight = optionsRef.current.clientHeight;
    const shouldScroll = optionHeight * (optionIndex + 1) > optionsHeight;

    if (shouldScroll || optionIndex === 1) {
      optionsRef.current.scrollTop = optionHeight * (optionIndex + 1) - optionsHeight;
    }
  }, [optionIndex, maxHeightOptions]);

  return (
    <StyledOptions
      animate="animate"
      initial="initial"
      exit="exit"
      variants={optionsVariants}
      ref={optionsRef}
      $maxHeight={maxHeightOptions}
      role="listbox"
      id={`listbox-${id}`}
      aria-labelledby={label}
      tabIndex={-1}
      data-testid="select-options"
    >
      {children}
    </StyledOptions>
  );
};

const StyledOptions = styled(motion.div)<{ $maxHeight: number }>`
  background-color: ${th.getColor('base000')};
  border-radius: ${th.getRadii('small')};
  box-shadow: ${th.getShadow('secondary')};
  max-height: ${({ $maxHeight }) => pxToRem($maxHeight)};
  overflow-y: auto;
  position: absolute;
  z-index: ${th.getZIndex('above')};
  outline: none;
  top: calc(100% + 4px);

  /* margin-left and width are hacks to expand the options over the other input borders  */
  margin-left: ${pxToRem(-1)};
  width: calc(100% + ${pxToRem(2)});

  &:hover {
    cursor: pointer;
  }
`;

type StyledOptionProps = { active: boolean; role: string; disabled?: boolean } & BoxProps;

export const StyledOption = styled(Box)<StyledOptionProps>`
  && {
    color: ${th.getColor('base900')};
    font-size: ${th.getFontSize('interactiveLg')};
    line-height: ${th.getLineHeight('interactiveLg')};
    font-weight: ${th.getFontWeight('semibold')};
    max-height: ${pxToRem(50)};

    &:not(:last-child) {
      border-bottom: 1px solid ${th.getColor('base100')};
    }

    &:hover {
      background-color: ${th.getColor('base050')};
    }

    ${({ active }) =>
      active &&
      css`
        &&:first-child {
          border-top-left-radius: ${th.getRadii('small')};
          border-top-right-radius: ${th.getRadii('small')};
        }

        &&:last-child {
          border-bottom-left-radius: ${th.getRadii('small')};
          border-bottom-right-radius: ${th.getRadii('small')};
        }

        font-weight: ${th.getFontWeight('bold')};
      `}

    ${({ disabled }) =>
      disabled &&
      css`
        color: ${th.getColor('base400')};

        &:hover {
          background-color: transparent;
        }
      `}
  }
`;
